
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Guest {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  origin_id?: string;
  created_at: string;
  updated_at: string;
  origins?: {
    id: string;
    name: string;
    description?: string;
  };
}

export interface Origin {
  id: string;
  name: string;
  description?: string;
}

export const useGuests = () => {
  return useQuery({
    queryKey: ['guests'],
    queryFn: async () => {
      console.log('🔍 Fetching guests...');
      const { data, error } = await supabase
        .from('guests')
        .select(`
          *,
          origins (
            id,
            name,
            description
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching guests:', error);
        throw error;
      }

      console.log('✅ Guests fetched successfully:', data);
      return data as Guest[];
    },
  });
};

export const useOrigins = () => {
  return useQuery({
    queryKey: ['origins'],
    queryFn: async () => {
      console.log('🔍 Fetching origins...');
      const { data, error } = await supabase
        .from('origins')
        .select('*')
        .order('name');

      if (error) {
        console.error('❌ Error fetching origins:', error);
        throw error;
      }

      console.log('✅ Origins fetched successfully:', data);
      return data as Origin[];
    },
  });
};

export const useGuestMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createGuest = useMutation({
    mutationFn: async (guestData: {
      name: string;
      email?: string;
      phone?: string;
      origin_id?: string;
    }) => {
      console.log('➕ Creating guest:', guestData);
      const { data, error } = await supabase
        .from('guests')
        .insert([guestData])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating guest:', error);
        throw error;
      }

      console.log('✅ Guest created successfully:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      toast({
        title: "Huésped creado",
        description: "El huésped ha sido agregado exitosamente",
      });
    },
    onError: (error: any) => {
      console.error('❌ Create guest error:', error);
      toast({
        title: "Error",
        description: `Error al crear el huésped: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateGuest = useMutation({
    mutationFn: async ({ id, ...guestData }: {
      id: string;
      name?: string;
      email?: string;
      phone?: string;
      origin_id?: string;
    }) => {
      console.log('📝 Updating guest:', id, guestData);
      const { data, error } = await supabase
        .from('guests')
        .update({
          ...guestData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating guest:', error);
        throw error;
      }

      console.log('✅ Guest updated successfully:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      toast({
        title: "Huésped actualizado",
        description: "Los datos del huésped han sido actualizados",
      });
    },
    onError: (error: any) => {
      console.error('❌ Update guest error:', error);
      toast({
        title: "Error",
        description: `Error al actualizar el huésped: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const deleteGuest = useMutation({
    mutationFn: async (id: string) => {
      console.log('🗑️ Deleting guest:', id);
      const { error } = await supabase
        .from('guests')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Error deleting guest:', error);
        throw error;
      }

      console.log('✅ Guest deleted successfully');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      toast({
        title: "Huésped eliminado",
        description: "El huésped ha sido eliminado del sistema",
      });
    },
    onError: (error: any) => {
      console.error('❌ Delete guest error:', error);
      toast({
        title: "Error",
        description: `Error al eliminar el huésped: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    createGuest,
    updateGuest,
    deleteGuest,
  };
};
